import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { api } from '../lib/api'

interface User {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  displayName: string | null
  role: string
  xp: number
  level: number
  streak: number
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (data: { email: string; password: string; firstName?: string; lastName?: string; displayName?: string }) => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function useAuthProvider(): AuthContextType {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Check session on mount
  useEffect(() => {
    api.getMe()
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { user } = await api.login(email, password)
    setUser(user)
  }, [])

  const signup = useCallback(async (data: { email: string; password: string; firstName?: string; lastName?: string; displayName?: string }) => {
    const { user } = await api.signup(data)
    setUser(user)
  }, [])

  const logout = useCallback(async () => {
    await api.logout()
    setUser(null)
  }, [])

  return { user, loading, login, signup, logout }
}
